// Shared CSV utilities — SGNL-211.
//
// Centralises the three pieces every CSV-exporting view needs:
//   * csvCell — RFC-4180 quoting + formula-injection defence
//   * toCsvRow — small helper that joins one row's cells
//   * downloadCsv — Blob + temporary-anchor pattern for client-side download
//
// Before this module existed, csvCell + the download sequence were
// duplicated in views/deals.ts + views/hygiene.ts. Code-reviewer F-06
// + architect F-01/F-02 in the SGNL-211 review pipeline converged on
// "extract to lib." This is that module.

/** RFC-4180 quote + formula-injection defence for one cell.
 *
 * - Empty / null / undefined → empty string.
 * - Values containing comma, quote, CR, LF → quoted; embedded
 *   quotes doubled.
 * - Values starting with a formula trigger (=, +, -, @) after any
 *   leading whitespace → prefixed with a leading apostrophe so
 *   spreadsheets don't interpret the cell as a formula.
 *
 * Security review (SGNL-211 SEC-02 + PR-Agent SEC importance 6):
 * the whitespace character class includes Unicode NBSP (U+00A0) and
 * narrow NBSP (U+202F) — both are stripped by Excel + Google Sheets
 * before formula evaluation — plus \t \r \n + ASCII space. Unicode
 * escapes are used (not literals) so the source doesn't depend on
 * editor normalisation of invisible characters.
 *
 * Moved from views/deals.ts in SGNL-211. Re-exported from deals.ts
 * so existing imports continue to work. */
export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value);
  const FORMULA_TRIGGER = /^[\t\r\n \u00A0\u202F]*[=+\-@]/;
  let escaped = FORMULA_TRIGGER.test(s) ? `'${s}` : s;
  const NEEDS_QUOTING = /[",\r\n]/;
  if (NEEDS_QUOTING.test(escaped)) {
    escaped = `"${escaped.replace(/"/g, '""')}"`;
  }
  return escaped;
}

/** Join one CSV row from its cell values. Each cell passes through
 * csvCell (so callers can pass raw strings + numbers without escaping
 * concerns). Returns the row string with no trailing newline. */
export function toCsvRow(
  cells: ReadonlyArray<string | number | null | undefined>,
): string {
  return cells.map(csvCell).join(",");
}

/** Trigger a client-side download of `content` as a file named
 * `filename`. Standard Blob + temporary-anchor + revoke pattern;
 * synchronous click() before revokeObjectURL is safe in all
 * mainstream browsers (the download is queued before revoke fires).
 *
 * Extracted from views/deals.ts + views/hygiene.ts (the click handlers
 * had the identical 9-line sequence). Single source of truth so a
 * future browser-quirk fix lands in one place.
 *
 * @param filename Suggested filename. Caller is responsible for the
 *                 file extension + any date suffix.
 * @param content  The CSV (or any text) content to download.
 * @param mimeType MIME type for the Blob. Defaults to text/csv;charset=utf-8. */
export function downloadCsv(
  filename: string,
  content: string,
  mimeType: string = "text/csv;charset=utf-8",
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
